/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/**
 * MarkdownSourceView — markdown source editor for Confluence Live Docs in
 * Markdown Mode.
 *
 * Behaviour:
 *  - On mount, convert the live ProseMirror document (`initialAdfDoc`) to
 *    markdown via `@atlassian/mdast-util-from-pm` and seed a CodeMirror 6 editor
 *    with it.
 *  - User edits live in the CodeMirror EditorState; we do NOT push them back
 *    into the ProseMirror editor on every keystroke (too expensive and would
 *    fight the collab provider).
 *  - On unmount (i.e. when the user switches back to WYSIWYG / preview), we
 *    read the editor's current doc, validate it parses back to ADF via
 *    `@atlaskit/editor-markdown-transformer`, and hand the markdown string
 *    off to the parent through `onMarkdownCommit`. The parent is responsible
 *    for applying it to the editor.
 *  - If the user did NOT actually edit the markdown buffer
 *    (`finalMarkdown === initialMarkdown`), we skip the commit entirely.
 *    This avoids a lossy ADF→markdown→ADF round-trip on every no-op visit
 *    to source view, which would otherwise reset collab cursors and undo
 *    history and could subtly mutate the doc (the markdown round-trip is
 *    not a strict identity).
 *  - If the markdown can't be parsed we `console.warn` and discard the change
 *    rather than crashing the editor. A user-facing surface for lossy
 *    round-trips is deferred — see TODO below.
 *
 * Re-seeding: callers may pass a `revision` prop. CodeMirror is destroyed
 * and re-mounted whenever `revision` changes, so the parent can force a
 * fresh seed (e.g. when re-entering source view with a new ADF snapshot).
 * The component is a single React tree across re-seeds — only the inner
 * CodeMirror state is recreated.
 *
 * The component intentionally takes a stable, narrow prop interface so other
 * agents (BRAVO's preview view + LivePageComponent plumbing, CHARLIE's tests)
 * can rely on it. Do not change the shape of `MarkdownSourceViewProps` without
 * coordinating with those agents.
 *
 * TODO(EDITOR-6558-source): surface lossy round-trips to the user (flag /
 * inline warning) instead of silently warning to the console.
 *
 * Ticket: https://hello.jira.atlassian.cloud/browse/EDITOR-6558
 * Parent epic: https://hello.jira.atlassian.cloud/browse/EDITOR-6490
 */
import {
	useCallback,
	useEffect,
	useRef,
	type MutableRefObject,
	type ReactElement,
} from 'react';

import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import {
	bracketMatching,
	defaultHighlightStyle,
	indentOnInput,
	syntaxHighlighting,
} from '@codemirror/language';
import { EditorState, StateEffect, StateField, RangeSetBuilder } from '@codemirror/state';
import {
	Decoration,
	type DecorationSet,
	EditorView,
	drawSelection,
	dropCursor,
	highlightActiveLine,
	keymap,
} from '@codemirror/view';
import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema';
import { markdownModeMessages as i18n } from '@atlaskit/editor-common/messages';
import type { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';
import type { SerializeFileMedia } from '@atlassian/mdast-util-from-pm/handlers/media';
import type { MarkdownSourceAnnotationRange } from '@atlassian/mdast-util-from-pm/types';

import {
	boldSourceViewCommand,
	italicSourceViewCommand,
	inlineCodeSourceViewCommand,
	strikeSourceViewCommand,
	blockquoteSourceViewCommand,
	bulletListSourceViewCommand,
	orderedListSourceViewCommand,
	heading1SourceViewCommand,
	heading2SourceViewCommand,
	heading3SourceViewCommand,
	heading4SourceViewCommand,
	heading5SourceViewCommand,
	heading6SourceViewCommand,
	computeSourceViewInlineFormatState,
	computeSourceViewBlockFormatState,
	computeSourceViewListFormatState,
} from '../source-view-utils';
import type { SourceViewCommand, SourceViewFormatState } from '../source-view-utils';
import { currentLineHasUnbalancedSyntax } from '../utils/hasUnbalancedMarkdownSyntax';
import { adfToMarkdown, markdownToAdf } from '../utils/markdown-conversion';
import { diffHunks, tryMergeDisjointDiffs } from '../utils/markdown-text-diff';

// ---------------------------------------------------------------------------
// Annotation decoration support
//
// Annotation marks (inline comments) from the ADF document are surfaced as
// highlighted spans in the CodeMirror source view. These are purely visual —
// the markdown string itself is clean (no annotation syntax). The highlights
// shift correctly as the user edits because CM6's RangeSet maps them through
// document changes via the StateField update function.
// ---------------------------------------------------------------------------

// noinspection JSVoidFunctionReturnValueUsed -- WebStorm seems to incorrectly infer the type of StateEffect as being a DOM thing
/** Effect dispatched to replace the annotation decoration set. */
const setAnnotationRangesEffect = StateEffect.define<MarkdownSourceAnnotationRange[]>();

/**
 * Returns a CM6 decoration mark for the given annotation ID.
 * The `id` attribute is set so that `document.getElementById(annotationId)`
 * finds this visible CM6 element (rather than the hidden ProseMirror span)
 * when the inline comment popup calculates its anchor position.
 */
function annotationMarkForId(annotationId: string): Decoration {
	return Decoration.mark({ class: 'cm-annotation-highlight', attributes: { id: annotationId } });
}

/**
 * Extract the current annotation character ranges from a CM6 DecorationSet,
 * matching each decoration back to its original annotation ID.
 *
 * CM6's `StateField` maps decorations through document changes automatically
 * (via `decorations.map(tr.changes)`), so after user edits the positions in
 * the DecorationSet reflect the shifted character offsets. We recover the
 * annotation ID by matching the shifted range against the original snapshot
 * list — first by exact position, then by any overlap (best-effort).
 */
function extractCurrentAnnotationRanges(
	decorations: DecorationSet,
	originalRanges: MarkdownSourceAnnotationRange[],
): MarkdownSourceAnnotationRange[] {
	const result: MarkdownSourceAnnotationRange[] = [];
	const cursor = decorations.iter();
	while (cursor.value !== null) {
		const { from, to } = cursor;
		// Try to match to an original annotation by exact position first,
		// then fall back to overlap — the id is what matters for re-application.
		const matched =
			originalRanges.find((a) => a.from === from && a.to === to) ??
			originalRanges.find((a) => a.from < to && a.to > from);
		// Only push if we found a matching annotation — an empty-string ID is
		// meaningless and would corrupt the ranges array passed downstream.
		if (matched) {
			result.push({ id: matched.id, from, to });
		}
		cursor.next();
	}
	return result;
}

/**
 * Keep the latest value in a stable ref for CodeMirror callbacks that are
 * intentionally created once per source-view mount.
 */
function useReactiveRef<T>(targetValue: T): MutableRefObject<T> {
	const ref = useRef<T>(targetValue);
	useEffect(() => {
		ref.current = targetValue;
	}, [targetValue]);
	return ref;
}

// noinspection JSVoidFunctionReturnValueUsed -- WebStorm seems to incorrectly infer the type of StateEffect as being a DOM thing
/**
 * CM6 StateField that holds the current annotation DecorationSet.
 *
 * On `setAnnotationRangesEffect`: rebuilds the decoration set from the new
 * ranges. On any other transaction: maps the existing decorations through
 * the document changes so character positions stay correct after edits.
 */
const annotationDecorationsField = StateField.define<DecorationSet>({
	create() {
		return Decoration.none;
	},
	update(decorations, tr) {
		// Check for a setAnnotationRangesEffect in this transaction.
		for (const effect of tr.effects) {
			if (effect.is(setAnnotationRangesEffect)) {
				const ranges = effect.value;
				if (ranges.length === 0) {
					return Decoration.none;
				}
				// Rebuild the decoration set from the new ranges.
				// Ranges must be sorted and non-overlapping for a single decoration
				// type — we sort by `from` and clamp to doc length.
				const docLen = tr.state.doc.length;
				const builder = new RangeSetBuilder<Decoration>();
				const sorted = [...ranges].sort((a, b) => a.from - b.from);
				for (const { from, to, id } of sorted) {
					const clampedFrom = Math.min(Math.max(from, 0), docLen);
					const clampedTo = Math.min(Math.max(to, 0), docLen);
					if (clampedFrom < clampedTo) {
						builder.add(clampedFrom, clampedTo, annotationMarkForId(id));
					}
				}
				return builder.finish();
			}
		}
		// No range update — map existing decorations through any document changes.
		return tr.docChanged ? decorations.map(tr.changes) : decorations;
	},
	provide(field) {
		return EditorView.decorations.from(field);
	},
});

const containerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	flex: '1',
	height: '100%',
	maxWidth: '1000px',
	marginInlineStart: 'auto',
	marginInlineEnd: 'auto',
	width: '100%',
});

const editorStyles = css({
	display: 'flex',
	flex: '1',
	minHeight: '300px',
	border: 'none',
	borderRadius: token('radius.large'),
	overflow: 'auto',
	backgroundColor: token('color.background.input'),
	width: '100%',
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& .cm-focused': {
		outline: 'none',
	},
});

/**
 * Stable public prop interface for `MarkdownSourceView`.
 *
 * Other agents depend on this contract — DO NOT change without coordination.
 */
export type MarkdownSourceViewProps = {
	/**
	 * Annotation (inline comment) character ranges for the current document.
	 *
	 * When provided (under `platform_editor_use_mdast_utils_conversion`),
	 * each range is rendered as a highlighted decoration in the CodeMirror
	 * source view — giving users a visual indication of which text spans
	 * carry inline comments, matching the highlight behaviour in the
	 * WYSIWYG editor.
	 *
	 * Ranges are character offsets into the markdown string. As the user
	 * edits the buffer, CodeMirror maps these decorations through changes
	 * automatically via the `StateField` — so comment highlights shift
	 * correctly when text is inserted or deleted around them.
	 *
	 * When the gate is off or no annotation marks are present, this prop
	 * is omitted / empty and no decorations are rendered.
	 */
	annotationRanges?: MarkdownSourceAnnotationRange[];
	/** The contentId of the Live Doc being edited (used for analytics / scoping). */
	contentId: string;
	/** ADF doc to convert to markdown on mount / re-seed. */
	initialAdfDoc: PMNode | DocNode;
	/**
	 * EDITOR-6558 Tier 1 (live WYSIWYG → source updates).
	 *
	 * The latest ADF doc from the live Fabric editor. The parent is expected
	 * to push updates here whenever the live doc changes (e.g. via a tap on
	 * `view.props.dispatchTransaction`, debounced ~150ms).
	 *
	 * On every change to this prop we:
	 *  1. Convert ADF → markdown (cheap; same path used at mount).
	 *  2. Skip if the new markdown matches the buffer verbatim, or if the
	 *     buffer round-trips to the same ADF (preserves the user's surface form).
	 *  3. Otherwise compute the user's unsynced diff (seed → buffer) and the
	 *     remote diff (seed → next). If the two ranges are disjoint, apply
	 *     the remote-only slice with a position shift through the user's
	 *     delta and bump the seed. If they overlap, stash the update and
	 *     wait for the next own-writeback to absorb it via `postWritebackAdfDoc`.
	 *
	 * Optional — when omitted, the source view stays at the snapshot it was
	 * seeded with (Tier 0 behaviour).
	 */
	liveAdfDoc?: PMNode | DocNode;
	/**
	 * Called when the user clicks on an annotation highlight (inline comment)
	 * in the CodeMirror source view. Receives the annotation ID of the clicked
	 * range. The CM6 highlight span has `id={annotationId}` so that
	 * `document.getElementById(annotationId)` finds the visible element for
	 * popup positioning (rather than the hidden ProseMirror DOM span).
	 *
	 * Only fired when `annotationRanges` is provided (i.e. under
	 * `platform_editor_use_mdast_utils_conversion`).
	 */
	onAnnotationClick?: (annotationId: string) => void;
	/**
	 * Called after every user edit (debounced alongside `onSourceMarkdownChange`)
	 * with the current CM6-tracked annotation ranges.
	 *
	 * CM6's `StateField` maps annotation decorations through document changes
	 * automatically — so these ranges reflect the shifted positions after the
	 * user inserts or deletes text. The parent stores these in a ref and uses
	 * them to re-apply annotation marks via `applyAnnotationRanges` in both
	 * the live writeback (`handleSourceMarkdownChange`) and the final commit
	 * (`handleMarkdownCommit`).
	 *
	 * Only fired when `annotationRanges` is provided (i.e. under
	 * `platform_editor_use_mdast_utils_conversion`).
	 */
	onAnnotationRangesChange?: (ranges: MarkdownSourceAnnotationRange[]) => void;
	/**
	 * Called on unmount AND on every revision change with the final markdown
	 * the user produced (only if the user actually edited the buffer — i.e.
	 * `finalMarkdown !== initialMarkdown`). The parent is responsible for
	 * parsing the markdown back to ADF and applying it to the editor.
	 */
	onMarkdownCommit?: (finalMarkdown: string) => void;
	/**
	 * Called on every CM6 selection or document change with a snapshot of the
	 * current format state at the cursor (bold active, in code block, heading
	 * level, etc.). Used by `MarkdownModeContentComponent` to push the state
	 * into the PM plugin state via `api.markdownMode.actions.setSourceFormatState`
	 * so that toolbar buttons can read `isActive` / `isDisabled` correctly
	 * while in source view.
	 *
	 * Only provided when `platform_editor_markdown_compatible_toolbar` is
	 * enabled — when the gate is off this prop is omitted.
	 */
	/**
	 * Called on mount with a dispatcher function that can run CM6 commands
	 * against the live CodeMirror EditorView, and called with `null` on
	 * unmount. Used by `MarkdownModeContentComponent` to wire up
	 * `api.markdownMode.actions.registerSourceCommandDispatch` so that the
	 * toolbar can route formatting commands to the source view.
	 *
	 * Only provided when `platform_editor_markdown_compatible_toolbar` is
	 * enabled — when the gate is off this prop is omitted.
	 */
	onRegisterCommandDispatch?: (dispatcher: ((cmd: SourceViewCommand) => boolean) | null) => void;
	onSourceFormatStateChange?: (state: SourceViewFormatState) => void;
	/**
	 * EDITOR-6558 Tier 2 (live source → WYSIWYG updates).
	 *
	 * Called whenever the user types in the source editor, debounced to
	 * `sourceChangeDebounceMs` (default 200ms). Receives the *current full*
	 * markdown buffer; the parent is responsible for parsing it back to ADF
	 * and applying it to the live editor (see LivePageEditorBody for the
	 * writeback path with collab-aware dispatch + echo suppression).
	 *
	 * Optional — when omitted, source view edits are batched until exit
	 * (Tier 0/1 behaviour) and committed via `onMarkdownCommit`.
	 *
	 * Echo behaviour: this callback also fires when CodeMirror is updated
	 * by `liveAdfDoc` (a programmatic update from the parent), but the
	 * markdown will be byte-equal to what the parent just pushed — the
	 * parent is responsible for not re-writing-back identical content. We
	 * could compare here too, but doing it once in the parent is simpler
	 * and avoids duplicating the comparison.
	 */
	onSourceMarkdownChange?: (markdown: string) => void;
	/**
	 * Post-writeback echo. The orchestrator pushes the post-rebase PM doc here
	 * after every successful own-writeback dispatch so the source view can
	 * re-seed: the buffer is replaced with the new markdown (suppressed so the
	 * user-edit listener doesn't see it), `seedMarkdownRef` bumps, and any
	 * deferred overlapping `liveAdfDoc` update is discarded (its content is
	 * already in the rebased PM doc).
	 */
	postWritebackAdfDoc?: PMNode | DocNode;
	/**
	 * Bumped by the parent whenever a fresh seed is required (e.g. on every
	 * entry to source view). Internally, when this changes we destroy the
	 * existing CodeMirror EditorView, fire `onMarkdownCommit` with the
	 * pending buffer, and rebuild from `initialAdfDoc`.
	 *
	 * Defaults to `0` so callers that don't care about re-seeding get the
	 * "mount once, commit on unmount" lifecycle.
	 */
	revision?: number;
	/** ProseMirror schema to use for both directions of conversion. */
	schema: Schema;
	/**
	 * Snapshot markdown pre-computed once by the parent under
	 * `platform_editor_md_source_sync_snapshot`. Used to seed CodeMirror's initial
	 * doc without re-converting, so the source view opens already populated.
	 * `undefined` = convert `initialAdfDoc` here on mount (gate off / fallback).
	 * Moves in lockstep with `revision`.
	 */
	seedMarkdown?: string;
	/**
	 * Builds a public, viewer-resolvable URL for a `media (type: "file")`
	 * node so it can be embedded as a CommonMark image. Confluence supplies
	 * this with a builder for `/wiki/download/attachments/{contentId}/{name}`;
	 * non-Confluence consumers can omit it and file media will be dropped.
	 *
	 * No tokens / signed URLs / `blob:` URLs should ever be returned here —
	 * the markdown is persisted and shared, so anything credentialed would
	 * leak. The proxy URL relies on the viewer's existing session cookie.
	 */
	serializeFileMedia?: SerializeFileMedia;
	/**
	 * Debounce window in ms for `onSourceMarkdownChange` while the user is
	 * actively typing on a line. Defaults to 2000ms.
	 *
	 * This debounce is reset on every keystroke. Two additional mechanisms
	 * reduce disruptive mid-edit writebacks:
	 *
	 * 1. **Line-change short-circuit**: if the cursor moves to a *different*
	 *    line while a debounce timer is pending, the timer is cancelled and
	 *    the writeback fires after a short 100ms grace period instead. This
	 *    means finishing a line and pressing Enter/clicking elsewhere syncs
	 *    the editor promptly without waiting the full 2000ms.
	 *
	 * 2. **Syntax balance guard**: even after the debounce fires, if the
	 *    cursor's current line has unbalanced syntax (e.g. `*ital` mid-word),
	 *    the writeback is rescheduled after 5000ms rather than firing
	 *    immediately. Only the current line is checked — a bare `*` on
	 *    another line (e.g. a bullet item) does not delay the writeback.
	 *    Fenced code blocks are checked document-wide. The retry guarantees
	 *    the doc eventually syncs even if the user leaves syntax unfinished.
	 *
	 * Lower values feel more "real-time" but burn more CPU on burst typing
	 * (every flush = full markdown→ADF parse + diff in the parent +
	 * ADF→markdown round-trip in the source view's guard).
	 */
	sourceChangeDebounceMs?: number;
};

/**
 * Markdown source view for Markdown-Mode Live Docs.
 *
 * Renders an editable CodeMirror 6 surface seeded with the markdown
 * representation of `initialAdfDoc`. The latest markdown is committed back to
 * the parent on unmount via `onMarkdownCommit` (see file header).
 */
export const MarkdownSourceView = ({
	contentId: _contentId,
	initialAdfDoc,
	seedMarkdown,
	schema,
	revision = 0,
	onMarkdownCommit,
	liveAdfDoc,
	postWritebackAdfDoc,
	onSourceMarkdownChange,
	sourceChangeDebounceMs = 2000,
	serializeFileMedia,
	annotationRanges,
	onAnnotationRangesChange,
	onAnnotationClick,
	onRegisterCommandDispatch,
	onSourceFormatStateChange,
}: MarkdownSourceViewProps): ReactElement => {
	const intl = useIntl();

	// Stash the file-media serialiser in a ref so changing the function
	// reference does not rebuild CodeMirror or invalidate the live-doc
	// effect. The function is read at call time inside `adfToMarkdown`.
	const serializeFileMediaRef = useReactiveRef(serializeFileMedia);

	// Stash the schema + commit callback in refs so the unmount cleanup can
	// keep its dep list narrow (we only want the seed/teardown to run on
	// revision change, not whenever the parent re-renders with a new
	// function ref).
	const schemaRef = useReactiveRef(schema);

	const onCommitRef = useReactiveRef(onMarkdownCommit);

	// EDITOR-6558 Tier 2 — keep the live source-change callback + debounce in
	// refs so the seed effect's CodeMirror extension list (which captures
	// these once at mount) always reads the latest values without rebuilding
	// the editor on every parent re-render.
	const onSourceMarkdownChangeRef = useReactiveRef(onSourceMarkdownChange);
	const sourceChangeDebounceMsRef = useReactiveRef(sourceChangeDebounceMs);

	// Latest ADF doc the parent has handed us, kept in a ref so the seed
	// effect can read the most-recent value without re-running on every
	// `initialAdfDoc` reference change. The seed effect deliberately reads
	// it only when `revision` changes.
	const initialAdfDocRef = useReactiveRef(initialAdfDoc);

	// Pre-computed markdown from the parent (sync-snapshot), mirrored to a ref and
	// updated during render so the seed effect reads the value for the current revision.
	const seedMarkdownPropRef = useRef(seedMarkdown);
	seedMarkdownPropRef.current = seedMarkdown;

	// Ref to the host element CodeMirror mounts into.
	const hostRef = useRef<HTMLDivElement | null>(null);
	// Ref to the live EditorView so the cleanup can read its doc.
	const viewRef = useRef<EditorView | null>(null);
	// The markdown the editor was *seeded* with for the current revision —
	// used to detect no-op commits and skip the writeback round-trip. Also
	// the baseline for the disjoint/overlap analysis in the live-refresh path.
	const seedMarkdownRef = useRef<string>('');
	// Most recently deferred `liveAdfDoc` update. Set when an incoming remote
	// change overlaps the user's unsynced typing. Discarded on next
	// `postWritebackAdfDoc` (its content is already in the post-rebase PM).
	const pendingLiveMarkdownRef = useRef<string | null>(null);

	const ariaLabel = intl.formatMessage(i18n.sourceAriaLabel);
	const ariaLabelRef = useReactiveRef(ariaLabel);

	// EDITOR-6558 Tier 2 — flag set while we're applying a programmatic
	// `liveAdfDoc` change to CodeMirror, so the resulting `updateListener`
	// fire is recognised as an echo and not pushed back to the parent. The
	// flag is set immediately before `view.dispatch({ changes })` and
	// cleared on the next microtask (CodeMirror's update listeners run
	// synchronously inside `dispatch`). Using a ref so the closure captured
	// inside the extension can read it.
	const suppressSourceChangeRef = useRef(false);
	// Debounce timer for `onSourceMarkdownChange` — scoped to the view so
	// teardown cleans it up.
	const sourceChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	// The line number (1-based) where the last doc change occurred.
	// Used to detect when the cursor has moved to a different line so we can
	// short-circuit the debounce and flush the pending writeback early.
	const lastEditLineRef = useRef<number | null>(null);

	// Stash onAnnotationRangesChange in a ref so the sourceChangeListener
	// (captured once at mount) always reads the latest callback without
	// rebuilding CodeMirror on every parent re-render.
	const onAnnotationRangesChangeRef = useReactiveRef(onAnnotationRangesChange);

	// Stash onAnnotationClick in a ref so the CM6 domEventHandlers click
	// extension (captured once at mount) always reads the latest callback.
	const onAnnotationClickRef = useReactiveRef(onAnnotationClick);

	// Stash onRegisterCommandDispatch in a ref so the effect below doesn't
	// re-run when the parent re-renders with a new function reference.
	const onRegisterCommandDispatchRef = useReactiveRef(onRegisterCommandDispatch);
	const onSourceFormatStateChangeRef = useReactiveRef(onSourceFormatStateChange);

	// Expose a stable dispatcher function via the onRegisterCommandDispatch
	// prop so MarkdownModeContentComponent can register it with the plugin
	// whenever api changes. The function reads viewRef at call time so it
	// always dispatches to the live CM6 EditorView.
	const dispatchCmd = useCallback(
		(cmd: SourceViewCommand): boolean => {
			const view = viewRef.current;
			if (!view) {
				return false;
			}
			return cmd(view);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- viewRef is a stable ref — intentionally not in deps.
		[],
	);

	// Register/unregister the dispatcher via the onRegisterCommandDispatch prop.
	// This fires on mount (register) and unmount (null). Re-registration when
	// the plugin rebuilds is handled by MarkdownModeContentComponent which
	// passes an updated onRegisterCommandDispatch whenever api changes.
	useEffect(() => {
		const onRegisterCommandDispatch = onRegisterCommandDispatchRef.current;
		onRegisterCommandDispatch?.(dispatchCmd);
		return () => {
			onRegisterCommandDispatch?.(null);
		};
	}, [dispatchCmd, onRegisterCommandDispatchRef]);

	// Annotation ranges — kept in a ref so the CM6 StateField (created once
	// at mount) can always read the latest value. Updated whenever the prop
	// changes (i.e. when the parent re-seeds on revision change or live update).
	const annotationRangesRef = useRef<MarkdownSourceAnnotationRange[]>(annotationRanges ?? []);
	useEffect(() => {
		annotationRangesRef.current = annotationRanges ?? [];
		// If the view is mounted, notify the StateField to recompute decorations.
		const view = viewRef.current;
		if (view) {
			view.dispatch({
				effects: setAnnotationRangesEffect.of(annotationRanges ?? []),
			});
		}
	}, [annotationRanges]);

	// Mount/seed CodeMirror on every `revision` change. Tear down + commit
	// on cleanup (revision change OR component unmount).
	useEffect(() => {
		const host = hostRef.current;
		if (!host) {
			return undefined;
		}
		const schema = schemaRef.current;
		const serializeFileMedia = serializeFileMediaRef.current;
		const onCommit = onCommitRef.current;
		const onAnnotationRangesChange = onAnnotationRangesChangeRef.current;

		// Seed markdown for this revision. Prefer the parent's pre-computed value
		// (sync-snapshot, avoids a second conversion); otherwise convert here.
		let seedMarkdownValue: string;
		const precomputedSeed = seedMarkdownPropRef.current;
		if (precomputedSeed !== undefined) {
			seedMarkdownValue = precomputedSeed;
		} else {
			try {
				const result = adfToMarkdown(initialAdfDocRef.current as PMNode, schema, {
					serializeFileMedia,
				});
				seedMarkdownValue = result.markdown;
			} catch (err) {
				// eslint-disable-next-line no-console
				console.warn(
					'[MarkdownSourceView] Failed to convert ADF doc to markdown; starting with empty buffer.',
					err,
				);
				seedMarkdownValue = '';
			}
		}
		seedMarkdownRef.current = seedMarkdownValue;

		// How long to wait before retrying a flush when the current line has
		// unbalanced syntax (e.g. user typed `*ital` and paused mid-word).
		// Long enough to give the user time to finish their syntax, but short
		// enough to guarantee the doc eventually syncs even if they leave it
		// unfinished (e.g. intentional bare `*` or just navigated away).
		const UNBALANCED_RETRY_MS = 5000;

		// Shared flush function — reads the current CM6 state and fires the
		// writeback callbacks. If the cursor's current line has unbalanced
		// markdown syntax (e.g. `*ital` mid-word) the flush is deferred by
		// UNBALANCED_RETRY_MS instead of firing immediately, giving the user
		// time to finish the syntax without corrupting the live PM doc.
		// Called both by the debounce timer and by the line-change short-circuit.
		const flushSourceChange = (editorView: EditorView) => {
			const markdownNow = editorView.state.doc.toString();
			const cursorPos = editorView.state.selection.main.head;

			// If the current line has unbalanced syntax, reschedule rather than
			// firing. Writing e.g. `*ital` into the PM editor would round-trip
			// back as `\*ital`, clobbering the source view. We defer instead of
			// suppressing indefinitely — worst case the user leaves a bare `*`
			// and it commits after UNBALANCED_RETRY_MS.
			if (currentLineHasUnbalancedSyntax(markdownNow, cursorPos)) {
				sourceChangeTimerRef.current = setTimeout(() => {
					sourceChangeTimerRef.current = null;
					const newEditorView = viewRef.current;
					if (newEditorView) {
						flushSourceChange(newEditorView);
					}
				}, UNBALANCED_RETRY_MS);
				return;
			}

			// Fire the annotation ranges change callback FIRST so that
			// currentAnnotationRangesRef is up-to-date before onSourceMarkdownChange
			// reads it to re-apply annotation marks. If onSourceMarkdownChange fired
			// first it would see stale/empty ranges on the first flush, stripping
			// all annotation marks from the live PM doc.
			const onRangesChange = onAnnotationRangesChangeRef.current;
			if (onRangesChange && annotationRangesRef.current.length > 0) {
				const currentRanges = extractCurrentAnnotationRanges(
					editorView.state.field(annotationDecorationsField),
					annotationRangesRef.current,
				);
				onRangesChange(currentRanges);
			}

			const cb = onSourceMarkdownChangeRef.current;
			if (cb) {
				cb(markdownNow);
			}
		};

		const sourceChangeListener = EditorView.updateListener.of((update) => {
			if (suppressSourceChangeRef.current) {
				return;
			}

			// Push format state on every selection or doc change so the toolbar
			// can reflect isActive / isDisabled correctly while in source view.
			if (update.docChanged || update.selectionSet) {
				onSourceFormatStateChangeRef.current?.({
					inline: computeSourceViewInlineFormatState(update.state),
					block: computeSourceViewBlockFormatState(update.state),
					list: computeSourceViewListFormatState(update.state),
				});
			}

			if (update.docChanged) {
				// Record which line the edit happened on so we can short-circuit
				// the debounce if the cursor moves away before it fires.
				const headPos = update.state.selection.main.head;
				lastEditLineRef.current = update.state.doc.lineAt(headPos).number;

				if (!onSourceMarkdownChangeRef.current) {
					return;
				}
				// Reset the debounce timer — 2000ms while the user is actively typing.
				if (sourceChangeTimerRef.current !== null) {
					clearTimeout(sourceChangeTimerRef.current);
				}
				sourceChangeTimerRef.current = setTimeout(() => {
					sourceChangeTimerRef.current = null;
					lastEditLineRef.current = null;
					const editorView = viewRef.current;
					if (!editorView) {
						return;
					}
					flushSourceChange(editorView);
				}, sourceChangeDebounceMsRef.current);
				return;
			}

			// Doc didn't change — check if the cursor has moved to a different line.
			// If there's a pending debounce timer and the cursor is now on a
			// different line than where the edit happened, short-circuit: cancel
			// the long timer and flush after a short 100ms grace period (to avoid
			// flushing on every arrow-key repeat).
			if (
				sourceChangeTimerRef.current !== null &&
				lastEditLineRef.current !== null &&
				update.selectionSet
			) {
				const headPos = update.state.selection.main.head;
				const currentLine = update.state.doc.lineAt(headPos).number;
				if (currentLine !== lastEditLineRef.current) {
					clearTimeout(sourceChangeTimerRef.current);
					sourceChangeTimerRef.current = setTimeout(() => {
						sourceChangeTimerRef.current = null;
						lastEditLineRef.current = null;
						const editorView = viewRef.current;
						if (!editorView) {
							return;
						}
						flushSourceChange(editorView);
					}, 100);
				}
			}
		});

		const state = EditorState.create({
			doc: seedMarkdownValue,
			extensions: [
				highlightActiveLine(),
				drawSelection(),
				dropCursor(),
				history(),
				indentOnInput(),
				bracketMatching(),
				markdown({ base: markdownLanguage }),
				// Default code-mirror highlight style — colourises markdown
				// tokens (headings, emphasis, code, links, etc.) using the
				// CM6 default theme.
				syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
				EditorView.lineWrapping,
				// Formatting shortcuts — mirror the WYSIWYG toolbar shortcuts.
				keymap.of([
					// Inline formatting
					{ key: 'Mod-b', run: boldSourceViewCommand },
					{ key: 'Mod-i', run: italicSourceViewCommand },
					{ key: 'Mod-e', run: inlineCodeSourceViewCommand },
					{ key: 'Mod-Shift-s', run: strikeSourceViewCommand },
					// Headings (Mod-Alt-1 through Mod-Alt-6, matching WYSIWYG)
					{ key: 'Mod-Alt-1', run: heading1SourceViewCommand },
					{ key: 'Mod-Alt-2', run: heading2SourceViewCommand },
					{ key: 'Mod-Alt-3', run: heading3SourceViewCommand },
					{ key: 'Mod-Alt-4', run: heading4SourceViewCommand },
					{ key: 'Mod-Alt-5', run: heading5SourceViewCommand },
					{ key: 'Mod-Alt-6', run: heading6SourceViewCommand },
					// Blockquote
					{ key: 'Mod-Shift-9', run: blockquoteSourceViewCommand },
					// Lists
					{ key: 'Mod-Shift-8', run: bulletListSourceViewCommand },
					{ key: 'Mod-Shift-7', run: orderedListSourceViewCommand },
				]),
				keymap.of([...defaultKeymap, ...historyKeymap]),
				EditorView.contentAttributes.of({ 'aria-label': ariaLabelRef.current }),
				EditorView.theme({
					'&': { height: '100%', width: '100%' },
					'.cm-scroller': fg('platform_editor_md_source_sync_snapshot')
						? // Token-aligned typography for the source view (ADS typography tokens
							// instead of raw monospace/14px/1.6 values).
							{ font: token('font.body'), fontFamily: token('font.family.code') }
						: { fontFamily: 'monospace', fontSize: '14px', lineHeight: '1.6' },
					// Annotation (inline comment) highlight — subtle background tint
					// matching the Confluence inline comment highlight colour.
					// cursor:pointer signals the span is interactive (clickable).
					'.cm-annotation-highlight': {
						backgroundColor: 'rgba(255, 196, 0, 0.3)',
						cursor: 'pointer',
					},
				}),
				// Annotation decorations — renders inline comment highlights.
				// Only active when annotation ranges are provided (i.e. under
				// platform_editor_use_mdast_utils_conversion). The StateField
				// maps ranges through document changes so highlights shift
				// correctly as the user edits.
				annotationDecorationsField,
				// Click handler for annotation highlights. When the user clicks
				// inside a `.cm-annotation-highlight` span, we look up which
				// annotation range contains the clicked character position and
				// fire `onAnnotationClick` with its ID. The parent dispatches
				// the appropriate ProseMirror command to open the comment popup
				// or panel — mirroring the click behaviour in the WYSIWYG view.
				EditorView.domEventHandlers({
					click(event, view) {
						const cb = onAnnotationClickRef.current;
						if (!cb) {
							return false;
						}
						const ranges = annotationRangesRef.current;
						if (!ranges.length) {
							return false;
						}
						// Use CM6's posAtCoords to get the character offset at the
						// click position, then find the annotation range that contains it.
						const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
						if (pos === null) {
							return false;
						}
						// Read the current (possibly-shifted) decoration positions from
						// the StateField so clicks work correctly after the user has
						// edited text around the annotation highlights.
						const decorationsField = view.state.field(annotationDecorationsField, false);
						if (!decorationsField) {
							// Fall back to the original snapshot ranges if the field
							// isn't present (e.g. annotationRanges wasn't provided).
							return false;
						}
						const currentRanges = extractCurrentAnnotationRanges(decorationsField, ranges);
						const clickedRange = currentRanges.find((range) => pos >= range.from && pos < range.to);
						if (!clickedRange) {
							return false;
						}
						cb(clickedRange.id);
						return true;
					},
				}),
				sourceChangeListener,
			],
		});

		const view = new EditorView({ state, parent: host });
		viewRef.current = view;

		onSourceFormatStateChangeRef.current?.({
			inline: computeSourceViewInlineFormatState(view.state),
			block: computeSourceViewBlockFormatState(view.state),
			list: computeSourceViewListFormatState(view.state),
		});

		// Seed the annotation decorations for this revision if ranges were provided.
		// We dispatch immediately after view creation so the initial highlights
		// are visible on mount without waiting for the next render cycle.
		if (annotationRangesRef.current.length > 0) {
			view.dispatch({
				effects: setAnnotationRangesEffect.of(annotationRangesRef.current),
			});
		}

		return () => {
			// Tier 2: drop any pending source-change debounce — we're tearing
			// down the view, the parent will get either the final commit
			// (below) or nothing.
			if (sourceChangeTimerRef.current !== null) {
				clearTimeout(sourceChangeTimerRef.current);
				sourceChangeTimerRef.current = null;
			}
			// Read the final markdown AND annotation ranges BEFORE destroying the view.
			const finalMarkdown = view.state.doc.toString();
			// Fire the final annotation ranges to the parent so handleMarkdownCommit
			// has the latest CM6-tracked positions (not the stale snapshot ranges).
			if (onAnnotationRangesChange && annotationRangesRef.current.length > 0) {
				const finalRanges = extractCurrentAnnotationRanges(
					view.state.field(annotationDecorationsField),
					annotationRangesRef.current,
				);
				onAnnotationRangesChange(finalRanges);
			}
			view.destroy();
			viewRef.current = null;

			if (!onCommit) {
				return;
			}
			// No-op guard: if the user didn't change the buffer, don't fire
			// a writeback. The markdown round-trip is lossy in general, so
			// committing the unchanged buffer would still mutate the live
			// doc (and reset collab cursors / undo history).
			if (finalMarkdown === seedMarkdownRef.current) {
				return;
			}
			try {
				// Validate by attempting to parse — if it throws we discard the
				// change. We deliberately do NOT pass the parsed PMNode out;
				// the parent re-parses against its own editor schema (which
				// may differ at the margins from ours) when applying.
				markdownToAdf(finalMarkdown, schema);
			} catch (err) {
				// eslint-disable-next-line no-console
				console.warn(
					'[MarkdownSourceView] Failed to parse markdown back to ADF on commit; discarding change.',
					err,
				);
				return;
			}
			onCommit(finalMarkdown);
		};
		// `revision` is the only dependency: a bump forces a destroy + re-seed.
		// Schema/commit/aria/initialAdfDoc are all read through refs above so
		// they don't reseed the editor (which would clobber the user's edits).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [revision]);

	// EDITOR-6558 Tier 1 — react to live WYSIWYG → source updates.
	//
	// We diff the new markdown against the current CodeMirror buffer and
	// dispatch a minimal-replace `ChangeSet`, so the user's caret survives
	// updates that don't touch the position they're editing at.
	//
	// `seedMarkdownRef` is updated on every successful diff-apply so a later
	// no-op-guard commit on unmount/revision-change still works correctly
	// (the "seed" for that revision is logically the most-recent live doc,
	// not the doc we mounted with).
	useEffect(() => {
		if (liveAdfDoc === undefined) {
			return;
		}
		const schema = schemaRef.current;
		const serializeFileMedia = serializeFileMediaRef.current;
		const view = viewRef.current;
		if (!view) {
			return;
		}
		let nextMarkdown: string;
		try {
			const result = adfToMarkdown(liveAdfDoc as PMNode, schema, {
				serializeFileMedia,
			});
			nextMarkdown = result.markdown;
		} catch (err) {
			// Live doc contains a node that the markdown serialiser can't
			// represent. Don't blow away the user's buffer — just skip.
			// eslint-disable-next-line no-console
			console.warn(
				'[MarkdownSourceView] Failed to convert live ADF doc to markdown; skipping live update.',
				err,
			);
			return;
		}
		const currentBuffer = view.state.doc.toString();
		if (nextMarkdown === currentBuffer) {
			// Echo from our own writeback (or no-op live update). Update the
			// seed reference so the no-op guard on the next commit reflects
			// the latest agreed-on text.
			seedMarkdownRef.current = nextMarkdown;
			return;
		}
		// EDITOR-6558 Option C — "source markdown is canonical".
		//
		// The user is typing in CodeMirror. Their buffer is THE truth. The
		// `liveAdfDoc` we just received may be semantically equal to what
		// the user typed but disagree on the markdown surface form (e.g.
		// `*ital` vs `\*ital` after the round-trip escape, or trailing
		// space → `&#x20;`). In those cases we must skip the diff-apply.
		//
		// Comparison strategy: re-serialise BOTH sides via `adfToMarkdown`
		// and compare the markdown strings. Reasoning:
		//  - JSON.stringify identity fails when plugins assign attrs like
		//    `localId` to the live doc that aren't in the parsed buffer.
		//  - `Node.eq` (with `schema.nodeFromJSON(liveAdfDoc)`) ALSO fails
		//    for the same reason — Node.eq is attr-strict.
		//  - Re-serialising both sides via the same pipeline drops
		//    non-semantic attrs (the markdown serialiser doesn't include
		//    `localId`), so byte-equal markdown ⇒ semantically equivalent
		//    docs. This is the same approach the integration test bed
		//    proved correct.
		try {
			const currentBufferAsAdf = markdownToAdf(currentBuffer, schema);
			const { markdown: bufferAsMarkdown } = adfToMarkdown(currentBufferAsAdf, schema, {
				serializeFileMedia,
			});
			if (bufferAsMarkdown === nextMarkdown) {
				// Don't bump seedMarkdownRef here — the user's buffer is the
				// authoritative seed for the no-op-commit guard. If they
				// switch back to wysiwyg without further edits, the commit
				// will correctly fire because their buffer differs from the
				// markdown we seeded with at mount.
				return;
			}
		} catch (err) {
			// `markdownToAdf` failed on the user's in-progress buffer
			// (e.g. unbalanced syntax). Fall through to the diff-apply
			// path below.
		}
		const changes = tryMergeDisjointDiffs(seedMarkdownRef.current, currentBuffer, nextMarkdown);
		if (!changes) {
			// Overlap with user's typing — defer until next postWritebackAdfDoc.
			pendingLiveMarkdownRef.current = nextMarkdown;
			return;
		}
		if (changes.length > 0) {
			// Suppress the user-edit listener for this programmatic dispatch —
			// CM6 update listeners run synchronously inside `view.dispatch`, so
			// it's safe to clear immediately after.
			suppressSourceChangeRef.current = true;
			try {
				view.dispatch({ changes });
			} finally {
				suppressSourceChangeRef.current = false;
			}
		}
		seedMarkdownRef.current = nextMarkdown;
		pendingLiveMarkdownRef.current = null;
	}, [liveAdfDoc, schemaRef, serializeFileMediaRef]);

	// Re-seed CM6 from the post-rebase PM doc the orchestrator pushed after
	// its own-writeback. Any deferred overlapping liveAdfDoc is already in PM
	// and is discarded.
	useEffect(() => {
		if (postWritebackAdfDoc === undefined) {
			return;
		}
		const view = viewRef.current;
		if (!view) {
			return;
		}
		const schema = schemaRef.current;
		let nextMarkdown: string;
		try {
			nextMarkdown = adfToMarkdown(postWritebackAdfDoc as PMNode, schema, {
				serializeFileMedia: serializeFileMediaRef.current,
			}).markdown;
		} catch (err) {
			// eslint-disable-next-line no-console
			console.warn(
				'[MarkdownSourceView] Failed to serialise post-writeback ADF; skipping re-seed.',
				err,
			);
			return;
		}
		const currentBuffer = view.state.doc.toString();
		// Minimal-replace via diffHunks instead of a full-buffer overwrite, so the
		// cursor stays put when it's outside any rebased remote hunk. Only the
		// rebased-flush case (post-rebase PM differs from buffer) actually dispatches.
		const hunks = diffHunks(currentBuffer, nextMarkdown);
		if (hunks.length > 0) {
			suppressSourceChangeRef.current = true;
			try {
				view.dispatch({ changes: hunks });
			} finally {
				suppressSourceChangeRef.current = false;
			}
		}
		seedMarkdownRef.current = nextMarkdown;
		pendingLiveMarkdownRef.current = null;
	}, [postWritebackAdfDoc, schemaRef, serializeFileMediaRef]);

	return (
		<div css={containerStyles} data-testid="markdown-source-view">
			<div css={editorStyles} ref={hostRef} data-testid="markdown-source-view--codemirror" />
		</div>
	);
};
